/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react'
import { remainingTokens, remainingLevelPercent } from '../../utils/utils.js'
import { Box, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Typography, Paper, Tooltip, IconButton } from '@mui/material'
import { ExpandMore, Info } from '@mui/icons-material'
import { sanitizeInput } from '../utils.js'

const getColor = (value) => {
  if (value >= 0.7) return '#4CAF50' // Bright green
  if (value >= 0.35) return '#FFEB3B' // Vibrant yellow
  if (value > 0) return '#F44336' // Bright red
  return '#B0B0B0' // Grey
}

const Gauge = ({ value, imageSrc, type, champion, size = 63 }) => {
  const adjustedValue = value === 0 ? 1 : value // ensure minimal value to render the circle

  const getImageTitle = (champion, type) => {
    if (!champion) return ''
    const { championName, championPoints, tokensEarned, championPointsUntilNextLevel } = champion
    return `${championName}: ${championPoints} points, ${Math.max(championPointsUntilNextLevel, 0)} points for next level${type === 'highLevel' ? `, ${tokensEarned} ${tokensEarned === 1 ? 'token' : 'tokens'}` : ''}`
  }

  return (
    <Box position="relative" display="inline-flex" alignItems="center">
      <CircularProgress
        variant="determinate"
        value={adjustedValue * 100}
        thickness={5}
        size={size}
        style={{
          color: getColor(value),
          transition: 'all 0.5s ease-out'
        }}
      />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <img src={imageSrc} alt="" style={{ width: '50px', height: '50px', borderRadius: '100%' }} title={getImageTitle(champion, type)} />
      </Box>
    </Box>
  )
}

export const TextDisplay = ({ data }) => {
  if (Object.keys(data).length === 0) return <div>No data available.</div>

  const [openSections, setOpenSections] = useState({})
  useEffect(() => {
    const initialOpenSections = {}
    Object.keys(data.levelData || {}).forEach(level => {
      initialOpenSections[level] = true
    })
    setOpenSections(initialOpenSections)
  }, [data.levelData])

  const toggleSection = (level) => setOpenSections(prev => ({ ...prev, [level]: !prev[level] }))

  const renderLevelDetails = (level, champions) => {
    const parentStyle = { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-around', margin: '5px 0' }
    const championDataStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '5px', flexBasis: 'calc(5%)' }

    return (
      <div style={parentStyle}>
        {champions.map((champion, index) => {
          const { championName, championLevel } = champion
          const imagePath = `photos/championImages/Icons/${sanitizeInput(championName)}.png`

          return (
            <div key={`level-${level}-champ-${index}`} style={championDataStyle}>
              {championLevel >= 5 && (
                <Box position="relative" display="inline-flex">
                  <Gauge value={remainingLevelPercent(champion)} size={79} />
                  <Box position="absolute" top={0} left={0} right={0} bottom={0} display="flex" alignItems="center" justifyContent="center">
                    <Gauge value={remainingTokens(champion)} imageSrc={imagePath} type="highLevel" champion={champion} />
                  </Box>
                </Box>
              )}
              {championLevel < 5 && (
                <Gauge value={remainingLevelPercent(champion)} imageSrc={imagePath} champion={champion}/>
              )}
              <span style={{ fontSize: 0 }}>{championName}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const formatChampionData = () => {
    const levels = [...Object.keys(data.levelData)].sort((a, b) => b - a)
    const highLevelChampions = levels
      .filter(level => parseInt(level, 10) >= 10)
      .reduce((acc, level) => {
        acc = acc.concat(data.levelData[level])
        return acc
      }, [])

    const otherLevels = levels.filter(level => parseInt(level, 10) < 10)

    return (
      <>
        {highLevelChampions.length > 0 && (
          <Accordion
            key="level-10-and-above"
            expanded={!!openSections['10-and-above']}
            onChange={() => toggleSection('10-and-above')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" component="div">Mastery 10+ Champions</Typography>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column' }}>
              {renderLevelDetails('10-and-above', highLevelChampions)}
            </AccordionDetails>
          </Accordion>
        )}

        {otherLevels.map(level => (
          <Accordion
            key={`level-${level}`}
            expanded={!!openSections[level]}
            onChange={() => toggleSection(level)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" component="div">Mastery {level} Champions</Typography>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column' }}>
              {renderLevelDetails(level, data.levelData[level])}
            </AccordionDetails>
          </Accordion>
        ))}
      </>
    )
  }

  const message = `There are ${new Intl.NumberFormat('de-DE').format(data.remainingPoints)} points left for all champions to be mastery 10 (${new Intl.NumberFormat('de-DE').format((data.remainingPoints / 400).toFixed(0))} ARAMs)`
  return (
    <div>
      <Box p={2}>
        <Paper elevation={4} style={{ padding: '20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="div" style={{ fontWeight: 'bold' }}>{message}</Typography>
          <Tooltip title="The outside circle is for points and the inside circle is for tokens for levels >= 5" arrow>
            <IconButton> <Info color="primary" style={{ fontSize: '2.5rem' }} /> </IconButton>
          </Tooltip>
        </Paper>

        {formatChampionData()}
      </Box>
    </div>
  )
}
